import { useState, useEffect } from "react";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import { Input } from "@/components/ui/input";

interface AddressAutocompleteProps {
    value: string;
    onChange: (address: string, placeDetails?: any) => void;
    className?: string;
    placeholder?: string;
}

const AddressAutocomplete = ({ value, onChange, className, placeholder }: AddressAutocompleteProps) => {
    const {
        ready,
        value: inputValue,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            /* You can add restrictions here, e.g.:
            componentRestrictions: { country: "pt" },
            types: ["address"],
            */
        },
        debounce: 400,
        defaultValue: value,
    });

    const [showSuggestions, setShowSuggestions] = useState(false);

    useEffect(() => {
        setValue(value);
    }, [value, setValue]);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        onChange(e.target.value, null);
        setShowSuggestions(true);
    };

    const handleSelect = async (suggestion: any) => {
        setValue(suggestion.description, false);
        clearSuggestions();
        setShowSuggestions(false);

        try {
            // Get detailed information about the selected place
            const results = await getGeocode({ placeId: suggestion.place_id });
            const addressComponents = results[0].address_components;

            // Extract address components
            const streetNumber = addressComponents.find(c => c.types.includes('street_number'))?.long_name || '';
            const route = addressComponents.find(c => c.types.includes('route'))?.long_name || '';
            const postalCode = addressComponents.find(c => c.types.includes('postal_code'))?.long_name || '';
            const city = addressComponents.find(c => c.types.includes('locality'))?.long_name || '';
            const region = addressComponents.find(c => c.types.includes('administrative_area_level_1'))?.long_name || '';
            const country = addressComponents.find(c => c.types.includes('country'))?.short_name || '';

            // Get coordinates if needed
            const { lat, lng } = await getLatLng(results[0]);

            // Create a structured address object
            const addressDetails = {
                formattedAddress: suggestion.description,
                streetNumber,
                route,
                streetAddress: streetNumber ? `${streetNumber} ${route}` : route,
                postalCode,
                city,
                region,
                country,
                coordinates: { lat, lng },
                placeId: suggestion.place_id,
                addressComponents
            };

            // Pass back both the formatted address string and the detailed object
            onChange(suggestion.description, addressDetails);

        } catch (error) {
            console.error("Error: ", error);
            onChange(suggestion.description, null);
        }
    };

    return (
        <div className="relative">
            <Input
                value={inputValue}
                onChange={handleInput}
                disabled={!ready}
                className={className}
                placeholder={placeholder}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            />
            {status === "OK" && showSuggestions && (
                <ul className="absolute z-10 w-full bg-white shadow-lg rounded-md mt-1 max-h-60 overflow-auto">
                    {data.map((suggestion) => (
                        <li
                            key={suggestion.place_id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleSelect(suggestion)}
                        >
                            {suggestion.description}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AddressAutocomplete; 